<template>
  <div>
    <div
      v-for="(row, index) in rows"
      :key="row.key"
      class="filter-row"
      :data-cy="`filter-row-${index}`"
    >
      <span class="filter-row__keyword">{{
        index === 0 ? 'Where' : 'And'
      }}</span>
      <TemplatePicker
        :id="`filter-field-${row.key}`"
        :templates="fieldChoices(index)"
        :label="fieldLabel(row.field)"
        search-placeholder="Search filters..."
        empty-text="No filters match"
        variant="light"
        block
        class="filter-row__field group-picker"
        @select="onFieldPick(index, $event)"
      >
        <template #button-content>
          <span class="group-picker__text">{{ fieldLabel(row.field) }}</span>
        </template>
      </TemplatePicker>

      <div class="filter-row__values">
        <template v-if="kind(row) === 'days'">
          <span class="filter-row__muted">between</span>
          <b-form-input
            v-model.number="row.min"
            type="number"
            :min="row.field === 'due' ? 0 : 1"
            placeholder="From"
            class="filter-row__value"
          />
          <span class="filter-row__muted">–</span>
          <b-form-input
            v-model.number="row.max"
            type="number"
            :min="row.field === 'due' ? 0 : 1"
            placeholder="To"
            class="filter-row__value"
          />
          <span v-if="row.field === 'due'" class="filter-row__hint">
            0 = today
          </span>
        </template>

        <template v-else-if="kind(row) === 'letters'">
          <span class="filter-row__muted">from</span>
          <b-form-input
            v-model="row.from"
            placeholder="A"
            maxlength="20"
            class="filter-row__value"
          />
          <span class="filter-row__muted">to</span>
          <b-form-input
            v-model="row.to"
            placeholder="Z"
            maxlength="20"
            class="filter-row__value"
          />
        </template>

        <DateFilterSelect
          v-else-if="kind(row) === 'dates' || kind(row) === 'birthday'"
          :row="row"
          :label="fieldLabel(row.field)"
          empty-text="Choose dates"
          :data-cy="`filter-dates-${index}`"
        />

        <MultiLocationSelect
          v-else-if="kind(row) === 'locations'"
          :ref="`picker-${row.key}`"
          v-model="row.items"
          class="filter-row__picker"
        />
        <MultiUserSelect
          v-else-if="kind(row) === 'managers'"
          :ref="`picker-${row.key}`"
          v-model="row.items"
          class="filter-row__picker"
        />
        <MultiUserSelect
          v-else-if="kind(row) === 'follow_up_managers'"
          :ref="`picker-${row.key}`"
          v-model="row.items"
          :include-unassigned="false"
          class="filter-row__picker"
        />
        <AccountTagFilter
          v-else-if="kind(row) === 'tags'"
          :ref="`picker-${row.key}`"
          class="filter-row__picker"
          @tags-changed="onTags(row, $event)"
        />
        <AccountStatusFilter
          v-else-if="kind(row) === 'statuses'"
          :ref="`picker-${row.key}`"
          class="filter-row__picker"
          @status-changed="onStatuses(row, $event)"
        />

        <template v-else-if="kind(row) === 'declined'">
          <DeclinedOriginFilter
            :ref="`picker-${row.key}`"
            class="filter-row__picker"
            @origins-changed="row.items = $event"
          />
          <span class="filter-row__muted">in the last</span>
          <b-form-input
            v-model.number="row.days"
            type="number"
            min="0"
            placeholder="Days"
            aria-label="Declined within days"
            class="filter-row__value"
          />
          <span class="filter-row__muted">days</span>
        </template>

        <b-form-radio-group
          v-else-if="kind(row) === 'toggle'"
          v-model="row.has"
          :options="hasChoices"
          :aria-label="`${row.field} has or does not have`"
          buttons
          button-variant="toggle"
          class="filter-row__toggle"
        />

        <b-form-checkbox-group
          v-else-if="kind(row) === 'follow_up_statuses'"
          v-model="row.items"
          :options="followUpStatusOptions"
          aria-label="Follow-up status"
          buttons
          button-variant="toggle"
          class="filter-row__toggle"
        />
      </div>

      <button
        type="button"
        class="filter-row__remove"
        title="Remove filter"
        :data-cy="`filter-remove-${index}`"
        @click="removeFilter(index)"
      >
        ×
      </button>
    </div>

    <button
      v-if="canAddFilter"
      type="button"
      class="add-filter"
      data-cy="add-filter"
      @click="addFilter"
    >
      + Add Filter
    </button>
  </div>
</template>

<script lang="ts">
import {
  FILTER_FIELDS,
  FOLLOW_UP_STATUS_OPTIONS,
  emptyRow,
  fieldKind
} from '@/views/MessageCenter/groupFilterRows.js'
import AccountStatusFilter from '@/views/MessageCenter/AccountStatusFilter.vue'
import AccountTagFilter from '@/views/MessageCenter/AccountTagFilter.vue'
import MultiLocationSelect from '@/views/MessageCenter/MultiLocationSelect.vue'
import MultiUserSelect from '@/views/MessageCenter/MultiUserSelect.vue'
import TemplatePicker from '@/components/MessageTemplate/TemplatePicker/TemplatePicker.vue'
import DateFilterSelect from '@/views/MessageCenter/DateFilterSelect.vue'
import DeclinedOriginFilter from '@/views/MessageCenter/DeclinedOriginFilter.vue'
import { Component, Prop, Vue } from 'vue-property-decorator'
import { FilterRow } from './types'

/**
 * The group editor's "Where / And" filter rows: every Customers filter, each usable once. Edits the
 * `rows` array in place; GroupForm turns it into the saved filter with groupFilterRows.filterFromRows.
 */
@Component({
  components: {
    AccountStatusFilter,
    AccountTagFilter,
    DateFilterSelect,
    DeclinedOriginFilter,
    MultiLocationSelect,
    MultiUserSelect,
    TemplatePicker
  }
})
export default class GroupFilterRowsEditor extends Vue {
  @Prop({ type: Array, required: true }) rows!: FilterRow[]

  // Constant option lists, as plain data: the template reads them and nothing changes them.
  followUpStatusOptions = FOLLOW_UP_STATUS_OPTIONS
  hasChoices = [
    { value: true, text: 'Has' },
    { value: false, text: "Doesn't have" }
  ]

  mounted() {
    this.$nextTick(this.fillPickers)
  }

  // One unchosen row at a time; every filter is usable once.
  get canAddFilter(): boolean {
    return (
      this.rows.every(row => row.field !== null) &&
      this.rows.length < FILTER_FIELDS.length
    )
  }

  kind(row: FilterRow): string | null {
    return fieldKind(row.field)
  }

  /**
   * The filters a row can switch to: each filter appears once, so another row's is left out. A-Z
   * within each menu group; the groups keep FILTER_FIELDS order, which the Customers filter lays out by.
   */
  fieldChoices(index: number) {
    const used = this.rows.filter((_, i) => i !== index).map(row => row.field)
    const groups = [...new Set(FILTER_FIELDS.map(field => field.group))]

    return FILTER_FIELDS.filter(({ value }) => !used.includes(value))
      .map(({ value, text, group }) => ({ value, text, group }))
      .sort(
        (a, b) =>
          groups.indexOf(a.group) - groups.indexOf(b.group) ||
          a.text.localeCompare(b.text)
      )
  }

  fieldLabel(field: string | null): string {
    if (field === null) return 'Choose a filter…'

    return FILTER_FIELDS.find(option => option.value === field)?.text ?? field
  }

  // A new filter starts blank; its old values would not fit the new inputs.
  onFieldPick(index: number, field: string) {
    const row = this.rows[index]
    if (row.field === field) return
    this.rows.splice(index, 1, {
      ...(emptyRow(field) as FilterRow),
      key: row.key
    })
  }

  // A new row starts with no filter chosen, so nothing is picked on the merchant's behalf.
  addFilter() {
    this.rows.push(emptyRow(null) as FilterRow)
  }

  removeFilter(index: number) {
    this.rows.splice(index, 1)
  }

  onTags(row: FilterRow, tags: Array<{ slug: string }>) {
    row.items = tags.map(tag => tag.slug)
  }

  onStatuses(row: FilterRow, statuses: string[]) {
    row.items = statuses
  }

  // A loaded group's values reach the pickers through their setters, not through the initial render.
  fillPickers() {
    for (const row of this.rows) {
      const [picker] = ([] as any[]).concat(
        this.$refs[`picker-${row.key}`] ?? []
      )
      const kind = this.kind(row)
      if (kind === 'tags') picker?.setTags?.(row.items)
      if (kind === 'statuses') picker?.setStatuses?.(row.items)
      if (kind === 'declined') picker?.setOrigins?.(row.items)
      // These two only fill on a later value change, so a loaded value needs setting by hand.
      if (
        kind === 'locations' ||
        kind === 'managers' ||
        kind === 'follow_up_managers'
      )
        picker?.setValue?.(row.items)
    }
  }
}
</script>

<style lang="scss" scoped>
$ink: #1f2937;
$line: #c7ced8;

.filter-row {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
  padding: 12px 16px;

  background: #f3f5f8;
  border-radius: 8px;
}

.filter-row__keyword {
  width: 52px;
  color: $darker-gray;

  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

// Wide enough for "Scheduled payment date"; it may shrink on a narrow screen, where the label ellipsizes.
.filter-row__field {
  flex: 0 1 250px;
  min-width: 140px;
}

.filter-row__muted {
  color: $darker-gray;

  font-size: 14px;
}

.filter-row__values {
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  min-width: 0;
}

.filter-row__value {
  max-width: 140px;
}

.filter-row__picker {
  flex: 1 1 260px;
  min-width: 0;
}

.filter-row__hint {
  color: $darker-gray;

  font-size: 12px;
}

// One block like the Customers filter's toggles: the group carries the outline and rounding.
.filter-row__toggle {
  overflow: hidden;

  background: #fff;
  border: 1px solid $line;
  border-radius: 8px;

  ::v-deep .btn-toggle {
    margin: 0;
    padding: 7px 14px;
    color: $ink;

    font-size: 14px;
    font-weight: 600;
    line-height: 1;

    background: transparent;
    border: 0;
    border-radius: 0;
    transition:
      color 0.15s,
      background-color 0.15s;

    + .btn-toggle {
      border-left: 1px solid $line;
    }

    &:hover {
      background: #eef2f6;
    }

    &.active {
      color: #fff;

      background: $button-blue;
    }

    &.focus,
    &:focus-within {
      outline: 2px solid $bright-blue;
      outline-offset: -2px;
      box-shadow: none;
    }
  }
}

.filter-row__remove,
.add-filter {
  background: transparent;
  border: 0;
  cursor: pointer;
}

.filter-row__remove {
  padding: 0 6px;
  color: $darker-gray;

  font-size: 20px;
  line-height: 1;
}

.add-filter {
  padding: 0;
  color: $ink;

  font-size: 14px;
  font-weight: 700;
  text-decoration: underline;
}
</style>
